<template>
  <AwSelectMenu
    ref="selectMenu"
    v-bind="props"
    v-model:search-term="searchTerm"
    :multiple="props.multiple"
    :placeholder="props.title"
    :items="displayedOptions"
    value-key="key"
    :class="props.triggerClass"
    content-class="min-w-fit max-h-96"
    :is-loading="loading"
    :max-visible-items="maxVisibleItems"
    :search-input="true"
    :model-value="selectedModelValue"
    :ignore-filter="true"
    :reset-search-term-on-select="false"
    @update:model-value="handleSelectionChange"
    @update:open="onOpenChange"
  >
    <template #default="{ modelValue }">
      <div v-if="!props.multiple">
        <div v-if="selectedLocations[0]">
          {{ selectedLocations[0].label }}
        </div>
        <div v-else class="color-gray-4">
          {{ props.title }}
        </div>
      </div>
      <SelectMenuBadgeList
        v-else
        v-slot="{ item }"
        icon="i-ph-map-pin-duotone"
        :list="Array.isArray(modelValue) ? modelValue : undefined"
        :loading="loading"
        :title="title"
        :max-visible-items="maxVisibleItems"
        @update:list="
          if ($event) {
            handleSelectionChange($event);
          }
        "
      >
        {{ labelForKey(item) }}
      </SelectMenuBadgeList>
    </template>
    <template #item-label="{ item }">
      <div class="address-container">
        <LazyAwIcon :name="getPlaceIcon(item.type)" color="var(--gray-3)" class="size-4" />
        <AwBadge size="sm" :label="LOCATION_TYPE_BADGES[item.type]" />
        <TextEllipsis>{{ formatOptionLabel(item) }}</TextEllipsis>
      </div>
    </template>
  </AwSelectMenu>
  <div v-if="props.withRange && isRadiusFilterRelevant" class="mt-2">
    <div>Rayon de recherche : {{ rangeRadius }}km</div>
    <input
      v-model.number="rangeRadius"
      type="range"
      min="10"
      max="100"
      step="10"
      :disabled="props.disabled"
      :style="{ '--range-progress': `${rangeProgress}%` }"
      class="range-input mt-2 w-full"
    />
  </div>
</template>

<script setup lang="ts">
import { useApi } from "~/composables/useApi";
import { useFuse } from "~/composables/useFuse";
import { useGoogleMaps } from "~/composables/useGoogleMaps";
import { watchDebounced } from "@vueuse/core";
import {
  isPlaceLocationFilter,
  locationFilterKey,
  type AdministrativeAreaLocationFilter,
  type LocationFilter,
  type LocationsFilter,
  type LocationType,
  type PlaceLocationFilter,
} from "@asap/shared/schemas/list";
import { removeAccents } from "@asap/shared/utils/string";

type Emits = {
  "update:value": [locations: LocationsFilter | LocationFilter | undefined];
};

const emit = defineEmits<Emits>();

const props = withDefaults(defineProps<Props>(), {
  value: undefined,
  title: "Localisation",
  disabled: false,
  multiple: true,
  withRange: false,
  searchTypes: () => ["city"],
  maxVisibleItems: 1,
  triggerClass: "w-full",
});

/** Badge shown before each row's name. */
const LOCATION_TYPE_BADGES: Record<LocationType, string> = {
  street: "Lieu",
  city: "Lieu",
  department: "Dept.",
  region: "Région",
};

/** Minimum characters required to trigger Google autocomplete */
const SEARCH_MIN_LENGTH = 2;
/** Debounce delay for Google autocomplete */
const SEARCH_DEBOUNCE_MS = 300;
/** Countries allowed in Google autocomplete */
const AUTOCOMPLETE_COUNTRIES = ["FR", "MC", "BE", "CH", "RO"] as const;
/** Google prediction types kept for a place search, in decreasing precision. */
const GOOGLE_PLACE_TYPES = ["subpremise", "premise", "route", "street_address", "locality"] as const;
const GOOGLE_TYPE_TO_LOCATION_TYPE: Record<(typeof GOOGLE_PLACE_TYPES)[number], PlaceLocationFilter["type"]> = {
  subpremise: "street",
  premise: "street",
  route: "street",
  street_address: "street",
  locality: "city",
};
/** Default radius applied to a freshly picked place. */
const DEFAULT_RADIUS_KM = 30;

/** Both referentials are closed and small; one page holds either of them whole. */
const REFERENTIAL_PAGE_SIZE = 200;

/** How far the fuzzy search tolerates a misspelling. */
const SEARCH_THRESHOLD = 0.4;

/** The number an area is known by, derived from its ISO 3166-2 code: `FR-69` → `69`, `FR-IDF` → `IDF`. */
const toLocalCode = (code: string) => code.slice(3);

/** A row of the `department` / `region` referential. */
type AdministrativeAreaRow = { code: string; name: string };

/** A location as the list handles it: the filter itself, plus the key rows are picked by. */
type LocationOption = LocationFilter & { key: string };

const toOption = (location: LocationFilter): LocationOption => ({ ...location, key: locationFilterKey(location) });

type Props = {
  title?: string;
  value?: LocationsFilter | LocationFilter;
  disabled?: boolean;
  multiple?: boolean;
  withRange?: boolean;
  searchTypes?: LocationType[];
  maxVisibleItems?: number;
  /** Class applied to the select trigger. Defaults to filling its container; pass e.g. "min-w-fit" to size to content instead (e.g. inside a flex filter row). */
  triggerClass?: string;
};

const { invoke } = useApi();

const departments = ref<AdministrativeAreaRow[]>([]);
const regions = ref<AdministrativeAreaRow[]>([]);

const selectedLocations = ref<LocationFilter[]>([]);
const placePredictions = ref<PlaceLocationFilter[]>([]);
/** Everything Google said about a prediction, by id. Indexing its terms too keeps `75011` matchable. */
const placeSearchTexts = ref<Record<string, string>>({});
const searchTerm = ref("");
/** The dropdown, to put the caret back in its search field after a pick. */
const selectMenu = ref<{ focusSearchInput: () => void } | null>(null);
/** Loading state for the Google round-trips */
const loading = ref(false);
/** Selected radius in km applied to every emitted place */
const rangeRadius = ref(DEFAULT_RADIUS_KM);
const rangeProgress = computed(() => ((rangeRadius.value - 10) / (100 - 10)) * 100);

const { loadPlaces } = useGoogleMaps();

let autocompleteService: google.maps.places.AutocompleteService | null = null;
let placesService: google.maps.places.PlacesService | null = null;

/** Which sources this instance draws from. */
const allowsPlaces = computed(() => props.searchTypes.some((type) => type === "street" || type === "city"));
const allowsDepartments = computed(() => props.searchTypes.includes("department"));
const allowsRegions = computed(() => props.searchTypes.includes("region"));

const getPlaceIcon = (type: LocationType) => {
  switch (type) {
    case "city":
      return "i-ph-building-apartment-duotone";
    case "department":
      return "i-ph-map-pin-simple-area-duotone";
    case "region":
      return "i-ph-map-trifold-duotone";
    default:
      return "i-ph-map-pin-duotone";
  }
};

const labelForKey = (key: unknown): string => {
  if (typeof key !== "string") return "";
  return selectedLocations.value.find((location) => locationFilterKey(location) === key)?.label ?? "";
};

const selectedModelValue = computed<string[] | string | undefined>(() => {
  const keys = selectedLocations.value.map(locationFilterKey);
  return props.multiple ? keys : keys[0];
});

const isRadiusFilterRelevant = computed(() => selectedLocations.value.some(isPlaceLocationFilter));

/** `Aisne (02)`. Only a department carries a number people know it by. */
const formatOptionLabel = (option: LocationFilter): string =>
  option.type === "department" ? `${option.label} (${toLocalCode(option.code)})` : option.label;

const toAreaOption = (
  type: AdministrativeAreaLocationFilter["type"],
  area: AdministrativeAreaRow
): AdministrativeAreaLocationFilter => ({ type, code: area.code, label: area.name });

/** Every area the caller accepts. */
const areaOptions = computed<AdministrativeAreaLocationFilter[]>(() => [
  ...(allowsDepartments.value ? departments.value.map((area) => toAreaOption("department", area)) : []),
  ...(allowsRegions.value ? regions.value.map((area) => toAreaOption("region", area)) : []),
]);

/** Areas and places in one index, so a department is ranked against a city on the same scale. */
const searchableOptions = computed<LocationOption[]>(() =>
  [...areaOptions.value, ...placePredictions.value].map(toOption)
);

const { results: matchedOptions } = useFuse(() => removeAccents(searchTerm.value), searchableOptions, {
  fuseOptions: {
    keys: [
      { name: "label", getFn: (option) => removeAccents(option.label) },
      { name: "code", getFn: (option) => (isPlaceLocationFilter(option) ? "" : toLocalCode(option.code)) },
      { name: "searchText", getFn: (option) => placeSearchTexts.value[option.key] ?? "" },
    ],
    threshold: SEARCH_THRESHOLD,
  },
  matchAllWhenSearchEmpty: true,
});

/**
 * Relevance alone while searching. With nothing typed there is none, so the picks lead rather than
 * sit scattered through the whole referential.
 */
const displayedOptions = computed<LocationOption[]>(() => {
  const ranked = matchedOptions.value.map((result) => result.item);
  if (searchTerm.value) return ranked;

  const selected = selectedLocations.value.map(toOption);
  const selectedKeys = new Set(selected.map((option) => option.key));
  return [...selected, ...ranked.filter((option) => !selectedKeys.has(option.key))];
});

const emitSelection = () => {
  const limited = props.multiple ? selectedLocations.value : selectedLocations.value.slice(0, 1);
  if (limited.length === 0) return emit("update:value", props.multiple ? [] : undefined);
  return emit("update:value", props.multiple ? limited : limited[0]);
};

const onOpenChange = (open: boolean) => {
  if (open) return;
  // The menu closes before the model value is written.
  nextTick(() => {
    placePredictions.value = [];
    searchTerm.value = "";
  });
};

const searchPlaces = (input: string) => {
  if (!autocompleteService || input.length < SEARCH_MIN_LENGTH) {
    placePredictions.value = [];
    placeSearchTexts.value = {};
    return;
  }

  loading.value = true;

  autocompleteService.getPlacePredictions(
    {
      input,
      types: [...GOOGLE_PLACE_TYPES],
      componentRestrictions: { country: [...AUTOCOMPLETE_COUNTRIES] },
    },
    (predictions, status) => {
      loading.value = false;

      if (status !== google.maps.places.PlacesServiceStatus.OK || !predictions) {
        placePredictions.value = [];
        placeSearchTexts.value = {};
        return;
      }

      const searchTexts: Record<string, string> = {};

      placePredictions.value = predictions.flatMap((prediction) => {
        const googleType = GOOGLE_PLACE_TYPES.find((type) => prediction.types.includes(type));
        if (!googleType) return [];

        const locationType = GOOGLE_TYPE_TO_LOCATION_TYPE[googleType];
        if (!props.searchTypes.includes(locationType)) return [];

        // The terms split the description, so a postal code stays matchable on its own.
        searchTexts[prediction.place_id] = removeAccents(
          [prediction.description, ...prediction.terms.map((term) => term.value)].join(" ")
        );

        // Coordinates only arrive with the place details, fetched once the user picks this row.
        return [
          {
            type: locationType,
            label: prediction.description,
            latitude: 0,
            longitude: 0,
            radius: rangeRadius.value,
            googlePlaceId: prediction.place_id,
          } satisfies PlaceLocationFilter,
        ];
      });

      placeSearchTexts.value = searchTexts;
    }
  );
};

/** Resolves the coordinates a picked Google prediction does not carry. */
const resolvePlaceCoordinates = async (place: PlaceLocationFilter): Promise<PlaceLocationFilter | null> => {
  const placeId = place.googlePlaceId;
  if (!placesService || !placeId) return null;

  const details = await new Promise<google.maps.places.PlaceResult | null>((resolve) => {
    placesService?.getDetails(
      { placeId, fields: ["place_id", "formatted_address", "geometry.location"] },
      (result, status) => resolve(status === google.maps.places.PlacesServiceStatus.OK && result ? result : null)
    );
  });

  const latitude = details?.geometry?.location?.lat();
  const longitude = details?.geometry?.location?.lng();
  if (latitude === undefined || longitude === undefined) return null;

  return { ...place, latitude, longitude, label: details?.formatted_address || place.label };
};

const handleSelectionChange = async (value: string[] | string | undefined) => {
  const nextKeys = Array.isArray(value) ? value : value ? [value] : [];
  const nextKeySet = new Set(nextKeys);

  const kept = selectedLocations.value.filter((location) => nextKeySet.has(locationFilterKey(location)));
  const keptKeys = new Set(kept.map(locationFilterKey));
  const added = nextKeys.flatMap((key) => {
    if (keptKeys.has(key)) return [];
    const option = displayedOptions.value.find((candidate) => candidate.key === key);
    return option ? [option] : [];
  });

  loading.value = added.some(isPlaceLocationFilter);

  try {
    const resolved: LocationFilter[] = [];
    for (const location of added) {
      if (!isPlaceLocationFilter(location)) {
        resolved.push({ type: location.type, code: location.code, label: location.label });
        continue;
      }
      const withCoordinates = await resolvePlaceCoordinates(location);
      if (withCoordinates) resolved.push(withCoordinates);
    }

    const next = [...kept, ...resolved];
    selectedLocations.value = props.multiple ? next : next.slice(-1);
    emitSelection();
  } finally {
    loading.value = false;
    placePredictions.value = [];
    searchTerm.value = "";
    // The click that selected a row took the caret with it.
    await nextTick();
    selectMenu.value?.focusSearchInput();
  }
};

/** Mirrors the prop into local state. A filter carries its own label, so nothing is re-resolved. */
const syncFromProp = (value?: LocationsFilter | LocationFilter) => {
  const normalized = value ? (Array.isArray(value) ? value : [value]) : [];
  selectedLocations.value = props.multiple ? normalized : normalized.slice(0, 1);

  const firstPlace = selectedLocations.value.find(isPlaceLocationFilter);
  if (firstPlace) rangeRadius.value = firstPlace.radius;
};

// Only Google needs debouncing; the referential is matched in the browser as the user types.
watchDebounced(
  searchTerm,
  (term) => {
    if (allowsPlaces.value) searchPlaces(term);
  },
  { debounce: SEARCH_DEBOUNCE_MS }
);

watch(() => props.value, syncFromProp, { immediate: true });

watch(rangeRadius, (radius) => {
  selectedLocations.value = selectedLocations.value.map((location) =>
    isPlaceLocationFilter(location) ? { ...location, radius } : location
  );
  emitSelection();
});

onMounted(async () => {
  if (allowsDepartments.value || allowsRegions.value) {
    const [departmentResponse, regionResponse] = await Promise.all([
      invoke({ path: "department", method: "GET", query: { pageSize: REFERENTIAL_PAGE_SIZE } }),
      invoke({ path: "region", method: "GET", query: { pageSize: REFERENTIAL_PAGE_SIZE } }),
    ]);
    if (departmentResponse.success) departments.value = departmentResponse.data.items;
    if (regionResponse.success) regions.value = regionResponse.data.items;
  }

  const Places = await loadPlaces();
  autocompleteService = new Places.AutocompleteService();
  placesService = new Places.PlacesService(document.createElement("div"));
});
</script>

<style scoped>
.address-container {
  display: flex;
  align-items: center;
  gap: 6px;
}

.range-input {
  -webkit-appearance: none;
  appearance: none;
  height: 16px;
  background: transparent;
}

.range-input::-webkit-slider-runnable-track {
  height: 6px;
  border-radius: 999px;
  background: linear-gradient(
    to right,
    var(--primary) 0%,
    var(--primary) var(--range-progress),
    var(--gray-6) var(--range-progress),
    var(--gray-6) 100%
  );
}

.range-input::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 999px;
  border: 2px solid var(--primary);
  background: var(--white);
  margin-top: -5px;
}
</style>
